import { useCallback, useState } from "react";
import { ApiError } from "../../../../lib/api";
import { scanEntitySite, type SiteScan } from "../../../../lib/siteScan";

/** Idle -> busy -> done/error. Idle exists because a scan is never automatic. */
export type EntityScanState =
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "done"; scan: SiteScan }
  | { kind: "error"; messageKey: string };

/**
 * Scans the website the register holds for one company.
 *
 * Never automatic, and for a stronger reason than the domain check: a scan
 * fetches the company's homepage plus a handful of extra documents from their
 * server. Browsing the register must not turn 90k businesses into a crawl, so
 * the request only leaves when an operator asks for it.
 */
export function useEntityScan(regCode: string) {
  const [state, setState] = useState<EntityScanState>({ kind: "idle" });

  const run = useCallback(
    async (refresh = false) => {
      setState({ kind: "busy" });
      try {
        setState({ kind: "done", scan: await scanEntitySite(regCode, refresh) });
      } catch (err) {
        const body = err instanceof ApiError ? (err.body as { error?: string }) : null;
        const messageKey =
          body?.error === "invalid_url"
            ? "scanner.error.invalidUrl"
            : body?.error === "no_website"
              ? "scanner.error.noWebsite"
              : body?.error === "forbidden"
                ? "scanner.error.forbidden"
                : "scanner.error.failed";
        setState({ kind: "error", messageKey });
      }
    },
    [regCode],
  );

  return { state, run };
}
